import { PROTOCOL_VERSION } from './constants';
import { decode } from './protocol';
import type { MatchConfig } from '../sim/types';
import type { Transport } from './Transport';
import { WebSocketTransport } from './WebSocketTransport';
import { normalizeRoomCode } from './room';

/*
 * הלקוח של חדר ה-relay.
 *
 * **הוא מסתיים בדיוק ברגע ש-`start` מגיע.** מכאן והלאה התעבורה שייכת ל-
 * ‏`LockstepSession`, והלקוח הזה כבר לא מדבר עליה — בדיוק כפי ש-`LockstepSession`
 * מתעלם מהודעות הלובי. שתי שכבות שחולקות סוקט אחד ואינן חולקות אחריות.
 *
 * **הבעלות על התעבורה עוברת ב-`detach()`.** בלי המסירה המפורשת הזו, פירוק הלובי
 * ב-React היה סוגר את הסוקט בדיוק כשהקרב מתחיל — וזה בדיוק סוג הבאג שנראה כמו
 * "היריב התנתק מיד".
 */

export type RelayError =
    /** לא הוגדרה כתובת שרת בבנייה. */
    | 'noServer'
    /** לא הצלחנו בכלל להגיע לשרת. */
    | 'network'
    | 'notFound'
    | 'full'
    /** הודעת פתיחה שאי אפשר לשחק לפיה — גרסה אחרת, מפה לא מוכרת, מושב לא נמצא. */
    | 'badStart'
    /** החיבור נפל ולא חזר. */
    | 'lost';

export type RelayPhase =
    | { readonly kind: 'idle' }
    | { readonly kind: 'connecting'; readonly room: string }
    | { readonly kind: 'waiting'; readonly room: string; readonly players: number }
    | {
          readonly kind: 'started';
          readonly room: string;
          readonly localPlayer: number;
          readonly config: MatchConfig;
      }
    | { readonly kind: 'error'; readonly reason: RelayError };

export interface RelayClientOptions {
    /** כתובת ה-Worker, למשל `https://crimson-front-relay.example.workers.dev`. */
    readonly baseUrl: string;
    /** מזהה יציב לכל לשונית. הוא שקובע מי יושב באיזה מושב, וגם מה שמאפשר חיבור מחדש. */
    readonly clientId: string;
    readonly onPhase: (phase: RelayPhase) => void;
    /** מוזרקים בטסטים. */
    readonly fetchImpl?: typeof fetch;
    readonly createTransport?: (url: string) => Transport;
}

/** בונה את שתי הכתובות מהבסיס. `ws`/`wss` נגזר מהסכימה, לא מנוחש. */
export function relayEndpoints(baseUrl: string): { readonly api: string; readonly socket: string } {
    const trimmed = baseUrl.replace(/\/+$/, '');
    const socket = trimmed.replace(/^http/, 'ws');
    return { api: `${trimmed}/api/room`, socket: `${socket}/api/room` };
}

export class RelayClient {
    private readonly options: RelayClientOptions;
    private transport: Transport | null = null;
    private unsubscribe: (() => void) | null = null;
    private phaseValue: RelayPhase = { kind: 'idle' };
    private detached = false;

    constructor(options: RelayClientOptions) {
        this.options = options;
    }

    get phase(): RelayPhase {
        return this.phaseValue;
    }

    /** פותח חדר חדש ומחזיר את הקוד שלו. */
    async create(mapId: string): Promise<void> {
        const { api } = relayEndpoints(this.options.baseUrl);
        const doFetch = this.options.fetchImpl ?? fetch;
        let room: string;
        try {
            const response = await doFetch(api, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ mapId, protocol: PROTOCOL_VERSION }),
            });
            if (!response.ok) {
                this.setPhase({ kind: 'error', reason: 'network' });
                return;
            }
            const body = (await response.json()) as { room?: unknown };
            room = typeof body.room === 'string' ? normalizeRoomCode(body.room) : '';
        } catch {
            this.setPhase({ kind: 'error', reason: 'network' });
            return;
        }
        if (room.length === 0) {
            this.setPhase({ kind: 'error', reason: 'network' });
            return;
        }
        this.connect(room);
    }

    /** מצטרף לחדר קיים. הקוד מנורמל כאן — המשתמש מקליד איך שנוח לו. */
    join(rawRoom: string): void {
        this.connect(normalizeRoomCode(rawRoom));
    }

    /**
     * מוסר את התעבורה למי שממשיך לקרב. מכאן הלקוח לא נוגע בה יותר,
     * ו-`dispose` לא יסגור אותה.
     */
    detach(): Transport | null {
        const transport = this.transport;
        this.detached = true;
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.transport = null;
        return transport;
    }

    dispose(): void {
        this.unsubscribe?.();
        this.unsubscribe = null;
        if (!this.detached) this.transport?.close();
        this.transport = null;
    }

    private connect(room: string): void {
        this.setPhase({ kind: 'connecting', room });
        const { socket } = relayEndpoints(this.options.baseUrl);
        const url = `${socket}/${room}/ws?client=${encodeURIComponent(this.options.clientId)}`;

        const transport =
            this.options.createTransport?.(url) ??
            new WebSocketTransport({
                url,
                onStatus: (status) => {
                    // סגירה סופית לפני `start` היא היחידה שמעניינת: אחריו האחריות
                    // עברה ל-`LockstepSession`, שמדווח בעצמו על יריב שנעלם.
                    if (status === 'closed' && this.phaseValue.kind !== 'started') {
                        this.setPhase({ kind: 'error', reason: 'lost' });
                    }
                },
            });

        this.transport = transport;
        this.unsubscribe = transport.onMessage((data) => this.handle(room, data));
    }

    private handle(room: string, data: string): void {
        const result = decode(data);
        // הודעות שאינן של הלובי (תור, גיבוב) מגיעות לכאן רק אם הן הקדימו את
        // ‏`start`. אין לנו מה לעשות איתן, וההתעלמות אינה מזיקה: הן ייחסמו ממילא.
        if (!result.ok) return;
        const message = result.message;

        if (message.kind === 'lobby') {
            if (message.state === 'full') {
                this.setPhase({ kind: 'error', reason: 'full' });
                return;
            }
            if (message.state === 'unknown') {
                this.setPhase({ kind: 'error', reason: 'notFound' });
                return;
            }
            this.setPhase({ kind: 'waiting', room, players: message.players });
            return;
        }

        if (message.kind === 'start') {
            // ‏`start` חוזר אחרי חיבור מחדש אינו מתחיל משחק שני.
            if (this.phaseValue.kind === 'started') return;
            const localPlayer = message.seats.indexOf(this.options.clientId);
            if (localPlayer < 0 || localPlayer >= message.config.slots.length) {
                this.setPhase({ kind: 'error', reason: 'badStart' });
                return;
            }
            this.setPhase({ kind: 'started', room, localPlayer, config: message.config });
        }
    }

    private setPhase(next: RelayPhase): void {
        this.phaseValue = next;
        this.options.onPhase(next);
    }
}
